// AGC: Offline Queue Service
// AGC: Enhanced offline queue supporting tasks, notes, and other offline actions
// AGC: Extends the existing mediaQueue.ts pattern to support multiple entity types

import { openDB, DBSchema, IDBPDatabase } from 'idb'
import type { OfflineMediaItem } from '../types/lotNotes'
import type { Task } from '../types/task'

// AGC: IndexedDB database name and version
const DB_NAME = 'droidfarm-offline-queue'
const DB_VERSION = 2  // AGC: Version 2 adds task support
const MEDIA_STORE = 'media_queue'
const TASK_STORE = 'task_queue'

/**
 * AGC: Offline task item queued for creation/update
 */
export interface OfflineTaskItem {
  id: string                    // AGC: Local temporary ID
  type: 'create' | 'update'     // AGC: Operation type
  taskData: Partial<Task>       // AGC: Task data to save
  userId: string
  createdAt: string
  retryCount: number
  lastAttempt?: string
  syncError?: string
}

/**
 * AGC: Database schema with both media and tasks
 */
interface OfflineQueueDB extends DBSchema {
  media_queue: {
    key: string
    value: OfflineMediaItem
    indexes: {
      'by-timestamp': string
      'by-user': string
      'by-lot': string
    }
  }
  task_queue: {
    key: string
    value: OfflineTaskItem
    indexes: {
      'by-timestamp': string
      'by-user': string
      'by-type': string
    }
  }
}

let dbInstance: IDBPDatabase<OfflineQueueDB> | null = null

/**
 * AGC: Initialize IndexedDB with both media and task stores
 */
const initDB = async (): Promise<IDBPDatabase<OfflineQueueDB>> => {
  if (dbInstance) return dbInstance

  dbInstance = await openDB<OfflineQueueDB>(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion) {
      // AGC: Create media store if it doesn't exist
      if (!db.objectStoreNames.contains(MEDIA_STORE)) {
        const mediaStore = db.createObjectStore(MEDIA_STORE, { keyPath: 'id' })
        mediaStore.createIndex('by-timestamp', 'createdAt')
        mediaStore.createIndex('by-user', 'userId')
        mediaStore.createIndex('by-lot', 'primaryLotId')
        console.log('🗄️ AGC: Media queue store initialized')
      }

      // AGC: Create task store if it doesn't exist (added in version 2)
      if (!db.objectStoreNames.contains(TASK_STORE)) {
        const taskStore = db.createObjectStore(TASK_STORE, { keyPath: 'id' })
        taskStore.createIndex('by-timestamp', 'createdAt')
        taskStore.createIndex('by-user', 'userId')
        taskStore.createIndex('by-type', 'type')
        console.log('🗄️ AGC: Task queue store initialized')
      }
    },
  })

  return dbInstance
}

// ============================================================================
// AGC: MEDIA QUEUE FUNCTIONS (from mediaQueue.ts)
// ============================================================================

/**
 * AGC: Add media item to offline queue
 */
export const queueMediaItem = async (item: OfflineMediaItem): Promise<void> => {
  try {
    const db = await initDB()
    await db.put(MEDIA_STORE, item)
    console.log(`📥 AGC: Media item queued:`, item.id)
  } catch (error) {
    console.error('AGC: Failed to queue media item', error)
    throw error
  }
}

/**
 * AGC: Get all queued media items
 */
export const getAllQueuedMediaItems = async (): Promise<OfflineMediaItem[]> => {
  try {
    const db = await initDB()
    const items = await db.getAll(MEDIA_STORE)
    console.log(`📋 AGC: Retrieved ${items.length} queued media items`)
    return items
  } catch (error) {
    console.error('AGC: Failed to get queued media items', error)
    return []
  }
}

/**
 * AGC: Remove media item from queue after successful upload
 */
export const removeQueuedMediaItem = async (itemId: string): Promise<void> => {
  try {
    const db = await initDB()
    await db.delete(MEDIA_STORE, itemId)
    console.log(`🗑️ AGC: Media item removed from queue:`, itemId)
  } catch (error) {
    console.error('AGC: Failed to remove queued media item', error)
    throw error
  }
}

// ============================================================================
// AGC: TASK QUEUE FUNCTIONS (new in this service)
// ============================================================================

/**
 * AGC: Add task to offline queue for later creation/update
 */
export const queueTask = async (item: OfflineTaskItem): Promise<void> => {
  try {
    const db = await initDB()
    await db.put(TASK_STORE, item)
    console.log(`📥 AGC: Task queued:`, item.id, item.type)
  } catch (error) {
    console.error('AGC: Failed to queue task', error)
    throw error
  }
}

/**
 * AGC: Get all queued tasks
 */
export const getAllQueuedTasks = async (): Promise<OfflineTaskItem[]> => {
  try {
    const db = await initDB()
    const items = await db.getAll(TASK_STORE)
    console.log(`📋 AGC: Retrieved ${items.length} queued tasks`)
    return items
  } catch (error) {
    console.error('AGC: Failed to get queued tasks', error)
    return []
  }
}

/**
 * AGC: Get queued tasks for a specific user
 */
export const getQueuedTasksByUser = async (userId: string): Promise<OfflineTaskItem[]> => {
  try {
    const db = await initDB()
    const items = await db.getAllFromIndex(TASK_STORE, 'by-user', userId)
    console.log(`📋 AGC: Retrieved ${items.length} queued tasks for user ${userId}`)
    return items
  } catch (error) {
    console.error('AGC: Failed to get queued tasks by user', error)
    return []
  }
}

/**
 * AGC: Update queued task (for retry count, error tracking)
 */
export const updateQueuedTask = async (
  itemId: string,
  updates: Partial<OfflineTaskItem>
): Promise<void> => {
  try {
    const db = await initDB()
    const item = await db.get(TASK_STORE, itemId)

    if (!item) {
      throw new Error('Queued task not found')
    }

    const updated = { ...item, ...updates }
    await db.put(TASK_STORE, updated)
    console.log(`✅ AGC: Queued task updated:`, itemId)
  } catch (error) {
    console.error('AGC: Failed to update queued task', error)
    throw error
  }
}

/**
 * AGC: Remove task from queue after successful sync
 */
export const removeQueuedTask = async (itemId: string): Promise<void> => {
  try {
    const db = await initDB()
    await db.delete(TASK_STORE, itemId)
    console.log(`🗑️ AGC: Task removed from queue:`, itemId)
  } catch (error) {
    console.error('AGC: Failed to remove queued task', error)
    throw error
  }
}

// ============================================================================
// AGC: UNIFIED QUEUE STATUS AND SYNC FUNCTIONS
// ============================================================================

/**
 * AGC: Get total count of pending items (media + tasks)
 */
export const getTotalQueueCount = async (): Promise<{
  media: number
  tasks: number
  total: number
}> => {
  try {
    const db = await initDB()
    const mediaCount = await db.count(MEDIA_STORE)
    const taskCount = await db.count(TASK_STORE)

    return {
      media: mediaCount,
      tasks: taskCount,
      total: mediaCount + taskCount,
    }
  } catch (error) {
    console.error('AGC: Failed to get queue count', error)
    return { media: 0, tasks: 0, total: 0 }
  }
}

/**
 * AGC: Check if device is offline
 */
export const isOffline = (): boolean => {
  return !navigator.onLine
}

/**
 * AGC: Process task queue - sync all pending tasks
 */
export const processTaskQueue = async (
  syncFunction: (item: OfflineTaskItem) => Promise<void>,
  onProgress?: (processed: number, total: number) => void
): Promise<{ processed: number; failed: number }> => {
  try {
    const items = await getAllQueuedTasks()
    const total = items.length

    if (total === 0) {
      console.log('📭 AGC: No tasks in queue to process')
      return { processed: 0, failed: 0 }
    }

    console.log(`🔄 AGC: Processing ${total} queued tasks...`)

    let processed = 0
    let failed = 0

    for (const item of items) {
      try {
        // AGC: Check retry limit (max 3 attempts)
        if (item.retryCount >= 3) {
          console.warn(`⚠️ AGC: Task ${item.id} exceeded retry limit, skipping`)
          failed++
          continue
        }

        // AGC: Sync task
        await syncFunction(item)

        // AGC: Remove from queue on success
        await removeQueuedTask(item.id)

        processed++

        if (onProgress) {
          onProgress(processed + failed, total)
        }

        console.log(`✅ AGC: Successfully synced queued task ${processed}/${total}`)
      } catch (error) {
        console.error(`❌ AGC: Failed to sync task ${item.id}`, error)

        // AGC: Increment retry count and store error
        await updateQueuedTask(item.id, {
          retryCount: item.retryCount + 1,
          lastAttempt: new Date().toISOString(),
          syncError: error instanceof Error ? error.message : 'Unknown error',
        })

        failed++

        if (onProgress) {
          onProgress(processed + failed, total)
        }
      }
    }

    console.log(`🎯 AGC: Task queue processing complete: ${processed} synced, ${failed} failed`)

    return { processed, failed }
  } catch (error) {
    console.error('AGC: Task queue processing error', error)
    throw error
  }
}

/**
 * AGC: Process media queue - upload all pending media
 */
export const processMediaQueue = async (
  uploadFunction: (item: OfflineMediaItem) => Promise<void>,
  onProgress?: (processed: number, total: number) => void
): Promise<{ processed: number; failed: number }> => {
  try {
    const items = await getAllQueuedMediaItems()
    const total = items.length

    if (total === 0) {
      console.log('📭 AGC: No media in queue to process')
      return { processed: 0, failed: 0 }
    }

    console.log(`🔄 AGC: Processing ${total} queued media items...`)

    let processed = 0
    let failed = 0

    for (const item of items) {
      try {
        // AGC: Check retry limit (max 3 attempts)
        if (item.retryCount >= 3) {
          console.warn(`⚠️ AGC: Media ${item.id} exceeded retry limit, skipping`)
          failed++
          continue
        }

        // AGC: Upload media
        await uploadFunction(item)

        // AGC: Remove from queue on success
        await removeQueuedMediaItem(item.id)

        processed++

        if (onProgress) {
          onProgress(processed + failed, total)
        }

        console.log(`✅ AGC: Successfully uploaded queued media ${processed}/${total}`)
      } catch (error) {
        console.error(`❌ AGC: Failed to upload media ${item.id}`, error)
        failed++

        if (onProgress) {
          onProgress(processed + failed, total)
        }
      }
    }

    console.log(`🎯 AGC: Media queue processing complete: ${processed} uploaded, ${failed} failed`)

    return { processed, failed }
  } catch (error) {
    console.error('AGC: Media queue processing error', error)
    throw error
  }
}

/**
 * AGC: Setup auto-sync for both media and tasks when connection restored
 */
export const setupAutoSync = (
  mediaUploadFunction: (item: OfflineMediaItem) => Promise<void>,
  taskSyncFunction: (item: OfflineTaskItem) => Promise<void>
): (() => void) => {
  const handleOnline = async () => {
    console.log('🌐 AGC: Connection restored, auto-syncing all queues...')

    try {
      // AGC: Process tasks first (they're usually smaller and faster)
      const taskResult = await processTaskQueue(taskSyncFunction)
      if (taskResult.processed > 0) {
        console.log(`✅ AGC: Auto-sync tasks complete: ${taskResult.processed} items synced`)
      }

      // AGC: Then process media uploads
      const mediaResult = await processMediaQueue(mediaUploadFunction)
      if (mediaResult.processed > 0) {
        console.log(`✅ AGC: Auto-sync media complete: ${mediaResult.processed} items uploaded`)
      }

      // AGC: Show summary if any failures
      if (taskResult.failed > 0 || mediaResult.failed > 0) {
        console.warn(
          `⚠️ AGC: ${taskResult.failed} tasks and ${mediaResult.failed} media items failed to sync`
        )
      }
    } catch (error) {
      console.error('AGC: Auto-sync failed', error)
    }
  }

  window.addEventListener('online', handleOnline)

  // AGC: Return cleanup function
  return () => {
    window.removeEventListener('online', handleOnline)
  }
}

/**
 * AGC: Clear all queues (for testing/reset)
 */
export const clearAllQueues = async (): Promise<void> => {
  try {
    const db = await initDB()
    await db.clear(MEDIA_STORE)
    await db.clear(TASK_STORE)
    console.log('🗑️ AGC: All queues cleared')
  } catch (error) {
    console.error('AGC: Failed to clear queues', error)
    throw error
  }
}
